import type { PortRequest } from '@/api/portage'

const TOLL_FREE = ['800', '888', '877', '866', '855', '844', '833']

/** "514 555-1234" → "+15145551234"; null when it is not a 10-digit North American number. */
export function toE164(raw: string): string | null {
  let digits = (raw || '').replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1)
  if (digits.length !== 10 || /^[01]/.test(digits) || /^\d{3}[01]/.test(digits)) return null
  return `+1${digits}`
}

export function isTollFree(raw: string): boolean {
  const e164 = toE164(raw)
  return !!e164 && TOLL_FREE.includes(e164.slice(2, 5))
}

/** "+15145551234" → "(514) 555-1234", the form printed on the letter. */
export function formatNanp(raw: string): string {
  const e164 = toE164(raw)
  if (!e164) return raw
  const d = e164.slice(2)
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
}

/** "2026-10-28" read as a calendar day (no time zone shift). */
export function parseDay(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d)
}

/** "11:30" → 11 h 30 in French, 11:30 a.m. in English, like the client emails. */
export function formatHour(hhmm: string, locale: string): string {
  const [h, m] = hhmm.split(':').map(Number)
  if (locale === 'fr') return m ? `${h} h ${String(m).padStart(2, '0')}` : `${h} h`
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'a.m.' : 'p.m.'}`
}

export function formatDay(iso: string | null | undefined, locale: string, withWeekday = true): string {
  if (!iso) return ''
  const day = parseDay(iso)
  const text = day.toLocaleDateString(locale === 'fr' ? 'fr-CA' : 'en-CA', {
    ...(withWeekday ? { weekday: 'long' } : {}),
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
  // "mardi 1 décembre" → "mardi 1er décembre".
  return locale === 'fr' && day.getDate() === 1 ? text.replace(/(^|\s)1(\s)/, '$11er$2') : text
}

export function formatShortDay(iso: string | null | undefined, locale: string): string {
  if (!iso) return ''
  return parseDay(iso).toLocaleDateString(locale === 'fr' ? 'fr-CA' : 'en-CA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/** The value stays the English name (what the server and the letter use); only the label follows the language. */
const PROVINCES_FR: Record<string, string> = {
  'British Columbia': 'Colombie-Britannique',
  'New Brunswick': 'Nouveau-Brunswick',
  'Newfoundland and Labrador': 'Terre-Neuve-et-Labrador',
  'Northwest Territories': 'Territoires du Nord-Ouest',
  'Nova Scotia': 'Nouvelle-Écosse',
  'Prince Edward Island': 'Île-du-Prince-Édouard',
  Quebec: 'Québec',
}

export const provinceLabel = (p: string, locale: string) => (locale === 'fr' ? PROVINCES_FR[p] || p : p)

/** "h2x1y4" → "H2X 1Y4". */
export function formatPostal(raw: string): string {
  const c = raw
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 6)
  return c.length > 3 ? `${c.slice(0, 3)} ${c.slice(3)}` : c
}

export const POSTAL_RE = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z] ?\d[ABCEGHJ-NPRSTV-Z]\d$/i
export const PO_BOX_RE =
  /\b(p\.?\s*o\.?\s*box|post\s+office\s+box|case\s+postale|bo[iî]te\s+postale|postal\s+box|general\s+delivery)\b|\b(c\.?\s*p|r\.?\s*r)\.?\s*\d+/i

/** Numbers still waiting to arrive: shown as transfer lines in the numbers table. */
export function pendingNumbers(t: PortRequest) {
  return t.numbers.filter((n) => n.status === 'attendu' || n.status === 'arrive' || n.status === 'rejete')
}
